import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import InputData from "./InputData";
import InputDataControl from "../support-extra/components/InputDataControl";
import Dropdown from "./Dropdown";
import CheckBox from "./CheckBox";
import InputTextArea from "./InputTextArea";
import InputIconSearch from "./InputIconSearch";
import TooltipBox from "./Tooltip";
import AdvancedWarehouseProductInfo from "./AdvancedWarehouseProductInfo";
import PurchaseAndSaleData from "./PurchaseAndSaleData";
import {
  contactLensesUseOptions,
  userData,
  contactLensesStructure,
  selectedOptic,
  focusElement,
  checkOfferActive,
} from "../js/utils";
import { post } from "../js/post";
import moment from "moment";
import { useSuppliers } from "../hooks/useSuppliers";
import { useCLTypes } from "../hooks/useCLTypes";
import { useBrands } from "../hooks/useBrands";
import { useModelsTradeNamesAndProducts } from "../hooks/useModelsTradeNamesAndProducts";

const ContactLensesData = forwardRef((props, ref) => {
  const typeList = useCLTypes().map((e) => ({ id: e.text, text: e.text }));
  const [currentProduct, setCurrentProduct] = useState(contactLensesStructure);
  const [currentProductFixed, setCurrentProductFixed] = useState(
    contactLensesStructure,
  );
  const [OPTCode, setOPTCode] = useState(null);
  const [rawBrandList, refreshBrands] = useBrands(2, props.supplierCode, "");
  const brandList = useMemo(() => {
    const brands = rawBrandList.map((e) => ({
      id: e,
      text: e,
    }));
    return [{ id: "", text: "" }, ...brands];
  }, [rawBrandList]);
  const [rawModelsList, refreshModels] = useModelsTradeNamesAndProducts(
    [2],
    currentProduct.idProveedor,
    currentProduct.marca !== "" ? [currentProduct.marca] : [],
  );
  const modelsList = useMemo(() => {
    return [
      { id: "", text: "" },
      ...rawModelsList.map((e) => ({
        id: e,
        text: e,
      })),
    ];
  }, [rawModelsList]);
  const supplierList = useSuppliers(2)[0].map((e) => ({
    id: e.id,
    text: e.nombre,
  }));
  const [productCode, setProductCode] = useState(props.code);
  const [toggleAdvancedData, setToggleAdvancedData] = useState(false);
  const [activeOffer, setActiveOffer] = useState(false);

  // console.log("currentProduct: ", currentProduct);

  const handleProductChange = (field, value) => {
    setCurrentProduct((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  async function getProductByCode(code, codeType) {
    if (code !== "") {
      const response = await post("ObtenerLenteContacto.php", {
        token: userData.token,
        codigo: code,
        tipoCodigo: codeType,
        idOptica: selectedOptic.id,
      });
      if (Object.keys(response).length === 0) {
        setCurrentProduct({ ...currentProduct, codigoOPTIWIN: OPTCode });
        setCurrentProductFixed({ ...currentProduct, codigoOPTIWIN: OPTCode });
        props.handleProductNotFound(code);
      } else {
        setCurrentProduct(response);
        setCurrentProductFixed(response);
        setOPTCode(response.codigoOPTIWIN);
        props.onSearchByCode(response.codigoOPTIWIN);
      }
      refreshBrands(response.idProveedor);
    }
  }

  function recalculateLabeling(marca = currentProduct.marca) {
    let etiquetado = "";

    if (marca && marca.trim() !== "") {
      etiquetado =
        marca.trim().length > 15 ? marca.trim().slice(0, 15) : marca.trim();
    }

    setCurrentProduct((prev) => ({
      ...prev,
      etiquetado,
    }));
  }

  const resetProduct = async (condition) => {
    if (condition > 1) {
      setTimeout(() => {
        focusElement(condition);
      }, 0);
    } else {
      refreshBrands(props.supplierCode);
      refreshModels(
        2,
        currentProduct.idProveedor,
        currentProduct.marca !== "" ? [currentProduct.marca] : [],
      );
      switch (condition) {
        case 0:
          const { marca, idProveedor, nombreProveedor } = currentProduct;
          setCurrentProduct(contactLensesStructure);
          setCurrentProductFixed(contactLensesStructure);
          setCurrentProduct((prev) => ({
            ...prev,
            marca: marca,
            idProveedor: idProveedor,
            nombreProveedor: nombreProveedor,
          }));
          setCurrentProductFixed((prev) => ({
            ...prev,
            marca: marca,
            idProveedor: idProveedor,
            nombreProveedor: nombreProveedor,
          }));
          break;
        case 1:
          setCurrentProduct((prev) => ({
            ...prev,
            codigoEANUPC: "",
          }));
          setCurrentProductFixed((prev) => ({
            ...prev,
            codigoEANUPC: "",
          }));
          break;
        default:
          break;
      }
    }
  };

  useImperativeHandle(ref, () => ({
    triggerAction: resetProduct,
  }));

  useEffect(() => {
    focusElement(productCode === -1 ? 502 : 501);
    if (productCode !== -1) {
      getProductByCode(productCode, -1);
    } else {
      setCurrentProduct((prevProduct) => ({
        ...prevProduct,
        idProveedor: props.supplierCode,
        marca: props.brand,
      }));
      setCurrentProductFixed((prevProduct) => ({
        ...prevProduct,
        idProveedor: props.supplierCode,
        marca: props.brand,
      }));
      refreshBrands(props.supplierCode);
    }
  }, []);

  useEffect(() => {
    props.handleProduct(currentProduct, currentProductFixed);
  }, [currentProduct]);

  useEffect(() => {
    setProductCode(props.code);
    if (props.code !== -1) {
      getProductByCode(props.code, -1);
    }
  }, [props.code]);

  useEffect(() => {
    setActiveOffer(
      checkOfferActive(
        currentProduct.fechaOfertaInicio,
        currentProduct.fechaOfertaFin,
      ),
    );
  }, [currentProduct.fechaOfertaInicio, currentProduct.fechaOfertaFin]);

  useEffect(() => {
    refreshModels(
      2,
      currentProduct.idProveedor,
      currentProduct.marca !== "" ? [currentProduct.marca] : [],
    );
  }, [currentProduct.idProveedor, currentProduct.marca]);

  return (
    <div className="opt-table-content grid-5" id="product-content-grid">
      {productCode !== -1 && (
        <>
          <div className="cell cell-label">Código de OPTIWIN</div>
          <div className="cell">
            <InputIconSearch
              tabIndex={501}
              type="type-only-numbers"
              size={0}
              maxLength={10}
              input={productCode}
              onChange={(e) => {
                setProductCode(e);
              }}
              onClear={() => {
                setProductCode("");
              }}
              onEnter={async () => {
                props.handleSearch(async () => {
                  await getProductByCode(productCode, 0);
                });
              }}
            ></InputIconSearch>
          </div>
          <div className="cell"></div>
          <div className="cell cell-label">PVP recomendado</div>
          <div className="cell">
            <InputData
              maxLength={20}
              inactive={true}
              type="type-float2"
              size={0}
              input={currentProduct.pvpRecomendado}
              onChange={() => {}}
            ></InputData>
          </div>
        </>
      )}
      <div className="cell cell-label">Código EAN/UPC</div>
      <div className="cell">
        <InputData
          tabIndex={502}
          maxLength={30}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.codigoEANUPC}
          onChange={(e) => {
            handleProductChange("codigoEANUPC", e);
          }}
        ></InputData>
      </div>
      <div className="cell"></div>
      {productCode !== -1 && (
        <>
          <div className="cell cell-label">
            PVP Oferta
            {activeOffer && (
              <img
                src="/images/check-orange.svg"
                loading="lazy"
                alt="Icon"
                className="cell-icon"
              ></img>
            )}
          </div>
          <div className="cell">
            <InputData
              inactive={true}
              type="type-float2"
              size={0}
              input={currentProduct.pvpOferta}
              onChange={() => {}}
            ></InputData>
          </div>
        </>
      )}
      {productCode === -1 && (
        <>
          <div className="cell cell-label">
            Fecha límite
            {activeOffer && (
              <img
                src="/images/check-orange.svg"
                loading="lazy"
                alt="Icon"
                className="cell-icon"
              ></img>
            )}
          </div>
          <div className="cell">
            <input
              type="datetime-local"
              max="9999-12-31T23:59"
              step={1}
              className="input-field input-cell"
              value={currentProduct.fechaLimite}
              onChange={(e) => {
                const value = e.target.value;
                handleProductChange(
                  "fechaLimite",
                  value === ""
                    ? ""
                    : moment(value).format("YYYY-MM-DD HH:mm:ss"),
                );
              }}
            ></input>
          </div>
        </>
      )}
      {productCode !== -1 && (
        <>
          <div className="cell cell-label">PVP</div>
          <div className="cell">
            <InputData
              tabIndex={503}
              type="type-float2"
              size={0}
              inactive={true}
              input={currentProduct.pvp}
              onChange={() => {}}
            ></InputData>
          </div>
          <div className="cell"></div>
          <div className="cell cell-label">PVP especial</div>
          <div className="cell">
            <InputData
              inactive={true}
              type="type-float2"
              size={0}
              input={currentProduct.pvpEspecial}
              onChange={() => {}}
            ></InputData>
          </div>
        </>
      )}
      <div className="cell combined-cells-5"></div>
      <div className="cell cell-label">Proveedor</div>
      <div className="cell combined-cells-4">
        <Dropdown
          optionList={[{ id: -1, text: "" }, ...supplierList]}
          size={0}
          disabled={true}
          op={currentProduct.idProveedor}
          onChange={async (e) => {
            handleProductChange("nombreProveedor", e.text);
            handleProductChange("idProveedor", e.id);
          }}
        ></Dropdown>
      </div>
      <div className="cell cell-label">Marca (*)</div>
      <div className="cell combined-cells-4">
        <Dropdown
          tabIndex={504}
          optionList={brandList}
          size={0}
          mandatory={false}
          op={currentProduct.marca}
          onChange={(e) => {
            handleProductChange("marca", e.text);
            recalculateLabeling(e.text);
          }}
        ></Dropdown>
      </div>
      <div className="cell cell-label">Nombre comercial (*)</div>
      <div className="cell combined-cells-4">
        <Dropdown
          tabIndex={505}
          optionList={modelsList}
          size={0}
          mandatory={false}
          op={currentProduct.nombreComercial}
          onChange={(e) => {
            handleProductChange("nombreComercial", e.text);
          }}
        ></Dropdown>
      </div>
      <div className="cell cell-label">Tipo</div>
      <div className="cell">
        <Dropdown
          tabIndex={506}
          disabled={false}
          optionList={[{ id: "", text: "" }, ...typeList]}
          size={0}
          op={currentProduct.tipo}
          onChange={(e) => {
            handleProductChange("tipo", e.text);
          }}
        ></Dropdown>
      </div>
      <div className="cell"></div>
      <div className="cell cell-label">Color</div>
      <div className="cell">
        <InputData
          tabIndex={509}
          maxLength={20}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.color}
          onChange={(e) => {
            handleProductChange("color", e);
          }}
        ></InputData>
      </div>
      <div className="cell cell-label">Desechable</div>
      <div className="cell">
        <div className="data-field c-pointer" onClick={() => {}}>
          <CheckBox
            tabIndex={507}
            active={currentProduct.desechable}
            onClick={() => {
              handleProductChange("desechable", !currentProduct.desechable);
            }}
          ></CheckBox>
        </div>
      </div>
      <div className="cell"></div>
      <div className="cell cell-label">Ref. Proveedor</div>
      <div className="cell">
        <InputData
          tabIndex={510}
          maxLength={20}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.referenciaProveedor}
          onChange={(e) => {
            handleProductChange("referenciaProveedor", e);
          }}
        ></InputData>
      </div>
      <div className="cell cell-label">Uso</div>
      <div className="cell">
        <Dropdown
          tabIndex={508}
          disabled={false}
          optionList={contactLensesUseOptions}
          size={0}
          op={currentProduct.uso}
          onChange={(e) => {
            handleProductChange("uso", e.text);
          }}
        ></Dropdown>
      </div>
      <div className="cell"></div>
      <div className="cell cell-label">Unidades</div>
      <div className="cell">
        <InputData
          tabIndex={511}
          type="type-integer"
          size={0}
          input={currentProduct.unidades}
          onChange={(e) => {
            handleProductChange("unidades", e);
          }}
        ></InputData>
      </div>
      <div className="cell cell-label">Etiquetado</div>
      <div className="cell combined-cells-4">
        <InputData
          tabIndex={512}
          maxLength={15}
          type="type-alphanum-ext"
          size={0}
          input={currentProduct.etiquetado}
          onChange={(e) => {
            handleProductChange("etiquetado", e);
          }}
        ></InputData>
        <button className="cell-icon" onClick={() => recalculateLabeling()}>
          <img
            src="/images/update-fill-blue.svg"
            loading="lazy"
            alt="Icon"
            className="cell-icon"
            data-tooltip-id="tt1"
          ></img>
          <TooltipBox
            id="tt1"
            content={
              <div className="cell">Recalcular etiquetado del producto</div>
            }
          ></TooltipBox>
        </button>
      </div>
      <div className="cell cell-label">Observaciones</div>
      <div className="cell combined-cells-4">
        <InputTextArea
          tabIndex={513}
          maxLength={200}
          type="type-full"
          size={0}
          input={currentProduct.observaciones}
          onChange={(e) => {
            handleProductChange("observaciones", e);
          }}
          extra="small-height"
        ></InputTextArea>
      </div>
      <div className="cell combined-cells-5">
        <div className="opt-table-content grid-11">
          <div className="cell cell-label">Esf</div>
          <div className="cell cell-label">Cil</div>
          <div className="cell cell-label">Eje</div>
          <div className="cell cell-label">Add</div>
          <div className="cell cell-label">Total</div>
          <div className="cell cell-label">Rad 1</div>
          <div className="cell cell-label">Rad 2</div>
          <div className="cell cell-label">Diá</div>
          <div className="cell cell-label">Hid</div>
          <div className="cell cell-label">ZO</div>
          <div className="cell cell-label">Ela</div>
          <div className="cell">
            <InputDataControl
              tabIndex={514}
              type="type-optofloat"
              size={0}
              input={currentProduct.esf}
              minValue=""
              maxValue=""
              step={0.25}
              numDecimales=""
              createPopupQuestion={props.createPopupQuestion}
              removePopupQuestion={props.removePopupQuestion}
              onChange={(e) => {
                handleProductChange("esf", e);
              }}
            ></InputDataControl>
          </div>
          <div className="cell">
            <InputDataControl
              tabIndex={515}
              type="type-optofloat"
              size={0}
              input={currentProduct.cil}
              minValue=""
              maxValue=""
              step={0.25}
              numDecimales=""
              createPopupQuestion={props.createPopupQuestion}
              removePopupQuestion={props.removePopupQuestion}
              onChange={(e) => {
                handleProductChange("cil", e);
              }}
            ></InputDataControl>
          </div>
          <div className="cell">
            <InputData
              tabIndex={516}
              type="type-integer"
              size={0}
              input={currentProduct.eje}
              onChange={(e) => {
                handleProductChange("eje", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputDataControl
              tabIndex={517}
              type="type-optofloat"
              size={0}
              input={currentProduct.add}
              minValue=""
              maxValue=""
              step={0.25}
              numDecimales=""
              createPopupQuestion={props.createPopupQuestion}
              removePopupQuestion={props.removePopupQuestion}
              onChange={(e) => {
                handleProductChange("add", e);
              }}
            ></InputDataControl>
          </div>
          <div className="cell">
            <InputData
              tabIndex={518}
              type="type-float2"
              size={0}
              input={currentProduct.total}
              onChange={(e) => {
                handleProductChange("total", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputData
              tabIndex={519}
              maxLength={6}
              type="type-alphanum-ext"
              size={0}
              input={currentProduct.rad1}
              onChange={(e) => {
                handleProductChange("rad1", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputData
              tabIndex={520}
              maxLength={6}
              type="type-alphanum-ext"
              size={0}
              input={currentProduct.rad2}
              onChange={(e) => {
                handleProductChange("rad2", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputData
              tabIndex={521}
              type="type-float2"
              size={0}
              input={currentProduct.dia}
              onChange={(e) => {
                handleProductChange("dia", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputData
              tabIndex={522}
              type="type-integer"
              size={0}
              input={currentProduct.hid}
              onChange={(e) => {
                handleProductChange("hid", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputData
              tabIndex={523}
              type="type-float2"
              size={0}
              input={currentProduct.zo}
              onChange={(e) => {
                handleProductChange("zo", e);
              }}
            ></InputData>
          </div>
          <div className="cell">
            <InputData
              tabIndex={524}
              type="type-float2"
              size={0}
              input={currentProduct.ela}
              onChange={(e) => {
                handleProductChange("ela", e);
              }}
            ></InputData>
          </div>
        </div>
      </div>
      <div className="cell combined-cells-5"></div>
      {productCode !== -1 && (
        <div className="combined-cells-5 opt-table-title">
          Datos avanzados de producto
          <button
            className="cell-icon"
            onClick={() => {
              setToggleAdvancedData(!toggleAdvancedData);
            }}
          >
            <img
              src={
                toggleAdvancedData
                  ? "/images/arrow-up-blue.svg"
                  : "/images/arrow-down-blue.svg"
              }
              loading="lazy"
              alt="Icon"
              className="cell-icon"
            ></img>
          </button>
        </div>
      )}
      {toggleAdvancedData && productCode !== -1 && (
        <AdvancedWarehouseProductInfo
          product={currentProduct}
          handleProductChange={handleProductChange}
        />
      )}
      {productCode === -1 && (
        <div className="cell combined-cells-5 opt-table-title">
          Datos de compra y venta
        </div>
      )}
      {productCode === -1 && (
        <PurchaseAndSaleData
          product={currentProduct}
          handleProductChange={handleProductChange}
          registerFromDeliveryNote={props.registerFromDeliveryNote}
          family={2}
        />
      )}
    </div>
  );
});

export default ContactLensesData;
